import { useCallback, useEffect, useState } from 'react'
import { BillInterestPayload, BillType, useGetBillInterestLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { UseFetchBillInterestResult } from '../interfaceTypes'

interface UseFetchBillInterestProps {
  billId: number | undefined
  clientId: string | undefined
  billType: BillType | undefined
  token: string | undefined
}

const useFetchBillInterest = ({
  billId,
  clientId,
  billType,
  token
}: UseFetchBillInterestProps): UseFetchBillInterestResult => {
  const [interestDue, setInterestDue] = useState<BillInterestPayload['interestDue']>(0)
  const [interestAccrued, setInterestAccrued] = useState<BillInterestPayload['interestAccrued']>(0)
  const [rate, setRate] = useState<BillInterestPayload['rate']>(0)

  const [getBillInterest, { loading, error, refetch }] = useGetBillInterestLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      console.log('error occured while fetching bill interest', error)
    },
    onCompleted(data) {
      if (data) {
        const { getBillInterest } = data || {}
        const { interestDue, interestAccrued, rate } = getBillInterest || {}
        setInterestDue(interestDue)
        setInterestAccrued(interestAccrued)
        setRate(rate)
      }
    }
  })

  const performGetBillInterest = useCallback(
    (isRefetch: boolean) => {
      if (!billId || !clientId || !token || !billType || billType === BillType.Fund) return

      if (isRefetch) {
        refetch({
          billInterestInput: {
            billId,
            clientId,
            token
          }
        })
      } else {
        getBillInterest({
          variables: {
            billInterestInput: {
              billId,
              clientId,
              token
            }
          }
        })
      }
    },
    [billId, clientId, token, billType, getBillInterest, refetch]
  )

  const refetchBillInterest = useCallback(() => {
    performGetBillInterest(true)
  }, [performGetBillInterest])

  useEffect(() => {
    performGetBillInterest(false)

    return () => {
      setInterestDue(0)
      setInterestAccrued(0)
      setRate(0)
    }
  }, [performGetBillInterest])

  return {
    interestDue,
    interestAccrued,
    rate,
    loading,
    error,
    refetch: refetchBillInterest
  }
}

export default useFetchBillInterest
